// App entry point - Simplified VLE Plus (Billing Only)
import { useState, useEffect, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { ThemeProvider } from "@/components/theme/ThemeProvider";
import { LanguageProvider } from "@/lib/i18n/LanguageContext";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { HelmetProvider } from "react-helmet-async";
import { DashboardSkeleton } from "@/components/ui/LoadingSkeleton";
import { OfflineIndicator } from "@/components/layout/OfflineIndicator";
import { ToolRouteGuard } from "@/components/guards/ToolRouteGuard";
import { VLE_TOOLS } from "@/hooks/useVLETools";

// Critical pages - load immediately
import Auth from "./pages/Auth";
import Index from "./pages/Index";
import Dashboard from "./pages/Dashboard";
import NotFound from "./pages/NotFound";

// Preload functions for route prefetching
const preloadSales = () => import("./pages/Sales");
const preloadDailyBook = () => import("./pages/DailyBook");
const preloadServices = () => import("./pages/Services");
const preloadCustomers = () => import("./pages/Customers");
const preloadSettings = () => import("./pages/Settings");

// Lazy load heavy pages with prefetch support
const Sales = lazy(preloadSales);
const DailyBook = lazy(preloadDailyBook);
const Services = lazy(preloadServices);
const KioskBook = lazy(() => import("./pages/KioskBook"));
const KioskReport = lazy(() => import("./pages/KioskReport"));
const PikVima = lazy(() => import("./pages/PikVima"));
const DocumentSlip = lazy(() => import("./pages/DocumentSlip"));
const SetuSuvidhaDocSlip = lazy(() => import("./pages/SetuSuvidhaDocSlip"));

const Expenses = lazy(() => import("./pages/Expenses"));
const Reports = lazy(() => import("./pages/Reports"));
const Settings = lazy(preloadSettings);
const Customers = lazy(preloadCustomers);
const CustomerDetail = lazy(() => import("./pages/CustomerDetail"));
const Install = lazy(() => import("./pages/Install"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const Contact = lazy(() => import("./pages/Contact"));
const About = lazy(() => import("./pages/About"));
const FindCSC = lazy(() => import("./pages/FindCSC"));
const VLEPublicProfile = lazy(() => import("./pages/VLEPublicProfile"));
const BlogListing = lazy(() => import("./pages/blog/BlogListing"));
const BlogPost = lazy(() => import("./pages/blog/BlogPost"));
const BlogCategoryArchive = lazy(() => import("./pages/blog/BlogCategoryArchive"));
const BlogTagArchive = lazy(() => import("./pages/blog/BlogTagArchive"));
const BlogAuthorArchive = lazy(() => import("./pages/blog/BlogAuthorArchive"));

// Business Settings Pages (VLE/Business Owner)
const BusinessDashboard = lazy(() => import("./pages/business/BusinessDashboard"));
const BusinessBilling = lazy(() => import("./pages/business/BusinessBilling"));
const SubscriptionBillingPage = lazy(() => import("./pages/business/SubscriptionBillingPage"));
const BusinessCommunity = lazy(() => import("./pages/business/BusinessCommunity"));
const BusinessTools = lazy(() => import("./pages/business/BusinessTools"));
const BusinessSettings = lazy(() => import("./pages/business/BusinessSettings"));
const AffiliateDashboard = lazy(() => import("./pages/business/AffiliateDashboard"));

// Platform Admin Pages (Platform Owner Only)
const PlatformDashboard = lazy(() => import("./pages/platform/PlatformDashboard"));
const PlatformSettings = lazy(() => import("./pages/platform/PlatformSettings"));
const PlatformTenants = lazy(() => import("./pages/platform/PlatformTenants"));
const PlatformSubscriptions = lazy(() => import("./pages/platform/PlatformSubscriptions"));
const PlatformAffiliates = lazy(() => import("./pages/platform/PlatformAffiliates"));
const PlatformBlog = lazy(() => import("./pages/platform/PlatformBlog"));
const PlatformBlogEditor = lazy(() => import("./pages/platform/PlatformBlogEditor"));

// Compact loading spinner
const PageLoader = () => (
  <div className="flex h-screen items-center justify-center bg-background">
    <div className="flex flex-col items-center gap-3">
      <div className="h-8 w-8 animate-spin rounded-full border-3 border-primary border-t-transparent" />
      <span className="text-sm text-muted-foreground">Loading...</span>
    </div>
  </div>
);

// Preload common routes when Dashboard loads
const usePreloadRoutes = () => {
  useEffect(() => {
    // Preload most used routes after initial render
    const timer = setTimeout(() => {
      preloadSales();
      preloadServices();
      preloadCustomers();
      preloadDailyBook();
      preloadSettings();
    }, 1000);
    return () => clearTimeout(timer);
  }, []);
};

// SPA redirect handler for hosting platforms that use 404.html redirect
const SPARedirectHandler = () => {
  const navigate = useNavigate();
  
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const redirect = params.get('redirect');
    
    if (redirect) {
      const decodedPath = decodeURIComponent(redirect);
      console.log('[SPARedirect] Redirecting to:', decodedPath);
      
      // Clean the URL and navigate using React Router
      window.history.replaceState(null, '', decodedPath);
      navigate(decodedPath, { replace: true });
    }
  }, [navigate]);
  
  return null;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error: any) => {
        if (error?.status === 401 || error?.message?.includes('JWT')) {
          return false;
        }
        return failureCount < 3;
      },
      staleTime: 1000 * 60 * 5,
    },
  },
});

// Protected route for all authenticated business owners
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading, isBillingInProgress, refreshSession, isSessionReady, profile, tenant, roles } = useAuth();
  const [isRecovering, setIsRecovering] = useState(false);
  
  useEffect(() => {
    if (!user && !isLoading && isBillingInProgress && !isRecovering) {
      console.log('[ProtectedRoute] No user during billing, attempting recovery...');
      setIsRecovering(true);
      refreshSession().finally(() => setIsRecovering(false));
    }
  }, [user, isLoading, isBillingInProgress, refreshSession, isRecovering]);
  
  if (isLoading || isRecovering) return (
    <div className="flex h-screen items-center justify-center p-6 bg-background">
      <DashboardSkeleton />
    </div>
  );
  
  if (!user && isBillingInProgress) {
    return (
      <div className="flex h-screen items-center justify-center p-6 bg-background">
        <div className="text-center">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto mb-4" />
          <p className="text-muted-foreground">Reconnecting session...</p>
        </div>
      </div>
    );
  }
  
  if (!user) return <Navigate to="/auth" replace />;
  
  // Wait for session to be fully ready
  if (!isSessionReady) {
    return (
      <div className="flex h-screen items-center justify-center p-6 bg-background">
        <div className="text-center">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto mb-4" />
          <p className="text-muted-foreground">Loading your workspace...</p>
          <p className="text-xs text-muted-foreground mt-2">
            {!profile ? 'Loading profile...' : !tenant ? 'Loading business...' : !roles.length ? 'Loading permissions...' : 'Almost ready...'}
          </p>
        </div>
      </div>
    );
  }
  
  return <>{children}</>;
}

// Platform Admin Route - Only for platform owners
function PlatformAdminRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading, isPlatformAdmin } = useAuth();
  
  if (isLoading) return (
    <div className="flex h-screen items-center justify-center p-6 bg-background">
      <DashboardSkeleton />
    </div>
  );
  
  if (!user) return <Navigate to="/auth" replace />;
  if (!isPlatformAdmin) return <Navigate to="/dashboard" replace />;
  
  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();
  if (isLoading) return (
    <div className="flex h-screen items-center justify-center bg-background">
      <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );
  if (user) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

const AppRoutes = () => {
  // Preload routes after dashboard loads
  usePreloadRoutes();
  
  return (
  <Suspense fallback={<PageLoader />}>
    <SPARedirectHandler />
    <Routes>
      <Route path="/" element={<Index />} />
      <Route path="/auth" element={<PublicRoute><Auth /></PublicRoute>} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/install" element={<Install />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/about" element={<About />} />
      
      {/* Public CSC Directory Routes */}
      <Route path="/find-csc" element={<FindCSC />} />
      <Route path="/csc/:slug" element={<VLEPublicProfile />} />
      <Route path="/csc/:state/:district/:taluka/:village/:businessSlug" element={<VLEPublicProfile />} />
      
      {/* Public Blog Routes */}
      <Route path="/blog" element={<BlogListing />} />
      <Route path="/blog/category/:slug" element={<BlogCategoryArchive />} />
      <Route path="/blog/tag/:slug" element={<BlogTagArchive />} />
      <Route path="/blog/author/:slug" element={<BlogAuthorArchive />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      
      {/* Protected Billing Routes - Core routes always accessible */}
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/sales" element={<ProtectedRoute><Sales /></ProtectedRoute>} />
      <Route path="/services" element={<ProtectedRoute><Services /></ProtectedRoute>} />
      <Route path="/customers" element={<ProtectedRoute><Customers /></ProtectedRoute>} />
      <Route path="/customers/:id" element={<ProtectedRoute><CustomerDetail /></ProtectedRoute>} />
      <Route path="/expenses" element={<ProtectedRoute><Expenses /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      
      {/* Tool-Protected Routes - Only accessible if tool is enabled */}
      <Route path="/daily-book" element={
        <ProtectedRoute>
          <ToolRouteGuard toolKey={VLE_TOOLS.DAILY_BOOK}>
            <DailyBook />
          </ToolRouteGuard>
        </ProtectedRoute>
      } />
      
      <Route path="/kiosk-book" element={
        <ProtectedRoute>
          <ToolRouteGuard toolKey={VLE_TOOLS.KIOSK_BANKING}>
            <KioskBook />
          </ToolRouteGuard>
        </ProtectedRoute>
      } />
      
      <Route path="/kiosk-report" element={
        <ProtectedRoute>
          <ToolRouteGuard toolKey={VLE_TOOLS.KIOSK_BANKING}>
            <KioskReport />
          </ToolRouteGuard>
        </ProtectedRoute>
      } />
      
      <Route path="/pik-vima" element={
        <ProtectedRoute>
          <ToolRouteGuard toolKey={VLE_TOOLS.PIK_VIMA}>
            <PikVima />
          </ToolRouteGuard>
        </ProtectedRoute>
      } />
      
      {/* Document Slip - Full Management */}
      <Route path="/document-slip" element={
        <ProtectedRoute>
          <DocumentSlip />
        </ProtectedRoute>
      } />
      
      {/* Setu Suvidha Doc Slip - Customer-facing print only */}
      <Route path="/setu-suvidha/doc-slip" element={
        <ProtectedRoute>
          <SetuSuvidhaDocSlip />
        </ProtectedRoute>
      } />
      
      {/* Business Settings Routes */}
      <Route path="/business/dashboard" element={<ProtectedRoute><BusinessDashboard /></ProtectedRoute>} />
      <Route path="/business/billing" element={<ProtectedRoute><BusinessBilling /></ProtectedRoute>} />
      <Route path="/business/subscription" element={<ProtectedRoute><SubscriptionBillingPage /></ProtectedRoute>} />
      <Route path="/business/affiliate" element={<ProtectedRoute><AffiliateDashboard /></ProtectedRoute>} />
      <Route path="/business/community" element={<ProtectedRoute><BusinessCommunity /></ProtectedRoute>} />
      <Route path="/business/tools" element={<ProtectedRoute><BusinessTools /></ProtectedRoute>} />
      <Route path="/business/settings" element={<ProtectedRoute><BusinessSettings /></ProtectedRoute>} />
      
      {/* Legacy redirects */}
      <Route path="/admin/dashboard" element={<Navigate to="/business/dashboard" replace />} />
      <Route path="/admin/billing" element={<Navigate to="/business/billing" replace />} />
      <Route path="/admin/community" element={<Navigate to="/business/community" replace />} />
      <Route path="/admin/tools" element={<Navigate to="/business/tools" replace />} />
      <Route path="/admin/settings" element={<Navigate to="/business/settings" replace />} />
      
      {/* Platform Admin Routes */}
      <Route path="/platform/dashboard" element={<PlatformAdminRoute><PlatformDashboard /></PlatformAdminRoute>} />
      <Route path="/platform/tenants" element={<PlatformAdminRoute><PlatformTenants /></PlatformAdminRoute>} />
      <Route path="/platform/subscriptions" element={<PlatformAdminRoute><PlatformSubscriptions /></PlatformAdminRoute>} />
      <Route path="/platform/affiliates" element={<PlatformAdminRoute><PlatformAffiliates /></PlatformAdminRoute>} />
      <Route path="/platform/blog" element={<PlatformAdminRoute><PlatformBlog /></PlatformAdminRoute>} />
      <Route path="/platform/blog/editor" element={<PlatformAdminRoute><PlatformBlogEditor /></PlatformAdminRoute>} />
      <Route path="/platform/blog/editor/:id" element={<PlatformAdminRoute><PlatformBlogEditor /></PlatformAdminRoute>} />
      <Route path="/platform/settings" element={<PlatformAdminRoute><PlatformSettings /></PlatformAdminRoute>} />
      
      <Route path="*" element={<NotFound />} />
    </Routes>
  </Suspense>
  );
};

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="light">
        <LanguageProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <AuthProvider>
                <AppRoutes />
                <OfflineIndicator />
              </AuthProvider>
            </BrowserRouter>
          </TooltipProvider>
        </LanguageProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
